'use client';

import { useEffect } from 'react';
import { motion, useCycle } from 'framer-motion';
import { getCookie } from 'cookies-next';

import { siteConfig } from '@/config/site';
import { useTranslations } from '@/hooks/useTranslations';
import { tenant } from '@/interfaces/common';

import promoImgae1 from '../../../public/images/usm_promo_img_1.png';
import promoImgae2 from '../../../public/images/usm_promo_img_2.png';
import promoImgae3 from '../../../public/images/usm_promo_img_3.png';
import infineonPromoImage from '../../../public/images/usm_promo_img_infineon.jpg';

const Dot = ({
  index,
  currentImageIndex,
}: {
  index: number;
  currentImageIndex: number;
}) => {
  const isActive = index === currentImageIndex;
  const width = isActive ? '32px' : '8px';
  return (
    <motion.div
      className={`h-2 rounded-full bg-white ${
        isActive ? 'opacity-[64%]' : 'opacity-[24%]'
      }`}
      style={{ width }}
      transition={{ duration: siteConfig.login_ui_slider_dot_duration }}
      animate={{ width }}
    />
  );
};
// Default fallback data for slides
const defaultSlides = [
  {
    heading: 'Setup your\nLab on our cloud!',
    description:
      'Step-by-step guided evaluation with ASSIST ME feature for EVK feature exploration. Control input and observe changes on output parameters from real lab instruments via live feed, application streaming and performance data.',
  },
  {
    heading: 'Explore EVKs & Plug-in',
    description:
      'Search among EVKs from multiple Semiconductor Manufactures to find the ready to use lab for your design evaluation. Click on "PLUG IN" to connect to your lab!',
  },
  {
    heading: 'Track changes, Insights and Observations',
    subheading: "Powered by TenXer Labs' LiveBench",
    description:
      'Track changes with progress log, find insights and observations on input-output relationships.',
  },
];

// Infineon-specific slide content
const infineonSlides = [
  {
    heading: 'Welcome to\nInfineon Live Lab',
    subheading: "Powered by TenXer Labs' LiveBench",
    description:
      'Experience seamless semiconductor evaluation with Infineon\'s cutting-edge evaluation kits. Access real-time lab instruments, live streaming, and comprehensive performance analytics.',
  },
];

interface PromoFrameProps {
  tenant?: tenant;
}

const PromoFrame = ({ tenant }: PromoFrameProps) => {
  // Check for Infineon tenant - multiple detection methods
  const tenantCookie = getCookie('tenant') as string; // Cookie name is 'tenant', not 'tenantName'
  
  // Parse the URL-encoded JSON cookie
  let tenantFromCookie = null;
  try {
    if (tenantCookie) {
      const decodedTenant = decodeURIComponent(tenantCookie);
      tenantFromCookie = JSON.parse(decodedTenant);
    }
  } catch (error) {
    console.warn('Failed to parse tenant cookie:', error);
  }
  
  // Also check URL parameters
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const tenantFromUrl = urlParams?.get('tenantName') || urlParams?.get('tenant');
  
  // Debug logging
  console.log('🖼️ PromoFrame Debug:', {
    tenantProp: tenant?.tenantName,
    tenantCookieRaw: tenantCookie?.substring(0, 100) + '...',
    tenantFromCookieParsed: tenantFromCookie?.tenantName,
    tenantFromUrl: tenantFromUrl,
    currentUrl: typeof window !== 'undefined' ? window.location.href : 'SSR'
  });
  
  // Check multiple sources for Infineon detection (prioritize prop)
  const isInfineonTenant = (tenant?.tenantName?.toLowerCase() === 'infineon') ||
                          (tenantFromCookie?.tenantName?.toLowerCase() === 'infineon') ||
                          (tenantFromUrl?.toLowerCase() === 'infineon') ||
                          (typeof window !== 'undefined' && window.location.href.includes('infineon'));
  
  // Select appropriate images based on tenant
  const images = isInfineonTenant 
    ? [infineonPromoImage] // Single Infineon image
    : [promoImgae1, promoImgae2, promoImgae3]; // Default 3 images
    
  console.log('🖼️ PromoFrame Result:', { 
    isInfineonTenant, 
    imageCount: images.length, 
    detectedTenantName: tenant?.tenantName || tenantFromCookie?.tenantName || 'unknown'
  });

  const [currentImageIndex, cycleImage] = useCycle(...Array.from(Array(images.length).keys()));
  const { translations, isLoading } = useTranslations('promo_frame');

  useEffect(() => {
    // Only cycle if there are multiple images
    if (images.length > 1) {
      const interval = setInterval(
        cycleImage,
        siteConfig.login_ui_slider_interval_duration
      );

      return () => clearInterval(interval);
    }
  }, [cycleImage, images.length]);

  if (isLoading || !translations) {
    return (
      <div className="relative h-full w-full animate-pulse rounded-2xl bg-gray-300">
        <div className="absolute inset-0 flex flex-col justify-between p-[80px]">
          <div className="flex w-full flex-col gap-4">
            <div className="h-9 w-64 rounded bg-gray-400" />
            <div className="h-20 w-80 rounded bg-gray-400" />
          </div>
          <div className="flex gap-[10px]">
            <div className="h-2 w-8 rounded-full bg-gray-400" />
            <div className="h-2 w-2 rounded-full bg-gray-400" />
            <div className="h-2 w-2 rounded-full bg-gray-400" />
          </div>
        </div>
      </div>
    );
  }

  // Select appropriate slides based on tenant
  const defaultSlideContent = isInfineonTenant ? infineonSlides : defaultSlides;
  const slides = translations?.slides || defaultSlideContent;

  return (
    <div className="relative h-full w-full">
      {/* Overlay */}

      {images.map((image, index) => (
        <div
          key={index}
          className={`${index !== currentImageIndex ? 'hidden' : ''} h-full`}
        >
          <motion.img
            src={image.src}
            alt="img"
            height={1000}
            width={1000}
            className="h-full w-full rounded-2xl object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              ease: 'easeOut',
              duration: siteConfig.login_ui_slider_img_transition,
            }}
          />
          {/* Text Overlay */}
          <div className="absolute inset-0 flex flex-col justify-between p-[80px]">
            <motion.div
              key={currentImageIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                ease: 'easeOut',
                duration: siteConfig.login_ui_slider_img_transition,
              }}
              className="flex w-full flex-col gap-4"
            >
              <h2
                className={`max-w-[420px] text-[32px] font-normal leading-[36px] tracking-wide text-white`}
                style={{ whiteSpace: 'pre-line' }}
              >
                {slides[index]?.heading || defaultSlides[index]?.heading}
              </h2>

              {(slides[index]?.subheading || defaultSlides[index]?.subheading) && (
                <h3 className="max-w-[420px] text-lg font-bold tracking-wide text-white opacity-[80%] mb-4">
                  {slides[index]?.subheading || defaultSlides[index]?.subheading}
                </h3>
              )}

              <p className="max-w-[420px] text-sm font-normal tracking-wide text-white opacity-[80%]">
                {slides[index]?.description ||
                  defaultSlides[index]?.description}
              </p>
            </motion.div>

            {/* Only show dots if there are multiple images */}
            {images.length > 1 && (
              <div className="flex gap-[10px]">
                {images.map((_, dotIndex) => (
                  <Dot
                    key={dotIndex}
                    index={dotIndex}
                    currentImageIndex={currentImageIndex}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PromoFrame;
